import "./styles.scss";

import { useTranslation } from "react-i18next";

import Parser from "@helpers/parser";
import { Text, Button, Icon } from "@fragments";
import { CommunityBanner } from "@components/Community";
import {
  IconArrowRight,
  IconPhoto,
  IconSchool,
  IconPercentage,
  IconClockHour4,
  IconCoin,
  IconFileText,
  IconFileDescription,
  IconBuildingCommunity,
  IconTimeline,
  IconClipboardText,
  IconCalendarEvent,
  IconFlag,
} from "@tabler/icons-react";

import { formatTimelineDate } from "@pages/Mentoring/helpers/mentoringTimeline";
import MentoringStatusChip from "../../components/MentoringStatusChip";
import MentoringTimeline from "@pages/Mentoring/components/MentoringTimeline";
import RevenueSection from "./components/RevenueSection";

// Duration unit labels resolve at render; the unit id is the API value.
const durationUnitLabel = (t, unit) =>
  ({
    days: t("durationUnits.days"),
    weeks: t("durationUnits.weeks"),
    months: t("durationUnits.months"),
  })[unit] ?? "";

/**
 * Creator — single subscribed mentoring (§6.11 detail). Hero (program identity +
 * dates) → status callouts → community revenue (insights link + chart) → a
 * two-column layout (sobre o programa · linha do tempo / comunidade).
 */
export default function MyMentoringDetailView({
  enrollment,
  loading,
  notFound,
  onAttach,
  revenue,
  onOpenInsights,
}) {
  const { t } = useTranslation("member");

  if (loading) {
    return (
      <div className="creator-mentoring px-4">
        <div className="cm-hero skel" />
        <div className="cm-grid">
          <div className="cm-skeleton skel" style={{ height: 220 }} />
          <div className="cm-skeleton skel" style={{ height: 220 }} />
        </div>
      </div>
    );
  }

  if (notFound || !enrollment) {
    return (
      <div className="creator-mentoring px-4">
        <div className="cm-empty">
          <Icon.Sphere size={72}>
            <IconSchool />
          </Icon.Sphere>
          <Text.h4 contrast={6}>{t("mentoringDetail.notFound.title")}</Text.h4>
          <Text.span contrast={5}>
            {t("mentoringDetail.notFound.description")}
          </Text.span>
        </div>
      </div>
    );
  }

  const { program, community, status, timestamps = {} } = enrollment;
  const splitPct =
    enrollment.effective_split_percentage ?? program?.split_percentage;
  const startLabel = timestamps.started_at
    ? formatTimelineDate(timestamps.started_at)
    : "";
  const endLabel = timestamps.ends_at
    ? formatTimelineDate(timestamps.ends_at)
    : "";

  return (
    <div className="creator-mentoring px-4 pt-5">
      {/* ── Hero: 4:3 banner (same ratio as the cards) + program identity ──── */}
      <div className="cm-hero">
        <div className="cm-hero-media">
          {program?.banner?.url ? (
            <img src={program.banner.url} alt={program.name} loading="lazy" />
          ) : (
            <div className="cm-hero-media-fallback">
              <IconPhoto size={30} />
            </div>
          )}
        </div>
        <div className="cm-hero-info">
          <MentoringStatusChip status={status} />
          <Text.h1 weight={500} maxLines={3}>
            {program?.name}
          </Text.h1>
          {program?.description && (
            <Text.p contrast={6} maxLines={3} className="cm-hero-desc">
              {program.description}
            </Text.p>
          )}
          {(startLabel || endLabel) && (
            <div className="cm-hero-meta">
              {startLabel && (
                <Text.span contrast={6} left={<IconCalendarEvent size={14} />}>
                  {t("mentoringDetail.hero.start", { date: startLabel })}
                </Text.span>
              )}
              {endLabel && (
                <Text.span contrast={6} left={<IconFlag size={14} />}>
                  {t("mentoringDetail.hero.end", { date: endLabel })}
                </Text.span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Status callouts ─────────────────────────────────────────────── */}
      {status === "awaiting_community" && (
        <div className="cm-callout cm-callout--action">
          <div className="cm-callout-main">
            <span className="cm-callout-icon">
              <IconBuildingCommunity size={18} />
            </span>
            <div className="cm-callout-text">
              <Text.h5 contrast={9} weight={500}>
                {t("mentoringDetail.callouts.attachTitle")}
              </Text.h5>
              <Text.span contrast={6}>
                {enrollment.next_steps ??
                  t("mentoringDetail.callouts.attachDescription")}
              </Text.span>
            </div>
          </div>
          <Button
            text={t("mentoringDetail.callouts.attachButton")}
            endIcon={<IconArrowRight />}
            color="primary"
            size="small"
            onClick={onAttach}
            preventLoading
          />
        </div>
      )}

      {status === "pending" && enrollment.next_steps && (
        <div className="cm-callout cm-callout--info">
          <IconClipboardText size={18} />
          <Text.span contrast={8}>{enrollment.next_steps}</Text.span>
        </div>
      )}

      {status === "split_only" && (
        <div className="cm-callout cm-callout--info">
          <IconCoin size={18} />
          <Text.span contrast={8}>
            {t("mentoringDetail.callouts.splitOnly", {
              date: fmtReadable(timestamps.split_ends_at),
            })}
          </Text.span>
        </div>
      )}

      {/* ── Sobre o programa  |  Linha do tempo / Comunidade ────────────── */}
      <div className="cm-grid">
        <div className="cm-col">
          <Card
            title={t("mentoringDetail.about.title")}
            icon={<IconFileDescription size={16} />}
          >
            <Row
              label={t("mentoringDetail.about.splitPercentage")}
              icon={<IconPercentage size={14} />}
              value={splitPct != null ? `${splitPct}%` : "—"}
              emphasis
            />
            <Row
              label={t("mentoringDetail.about.duration")}
              icon={<IconClockHour4 size={14} />}
              value={
                program?.duration_value
                  ? `${program.duration_value} ${durationUnitLabel(t, program.duration_unit)}`.trim()
                  : t("mentoringDetail.about.durationUndefined")
              }
            />
            <Row
              label={t("mentoringDetail.about.splitWindow")}
              icon={<IconCoin size={14} />}
              value={
                program?.split_duration_value
                  ? `${program.split_duration_value} ${durationUnitLabel(t, program.split_duration_unit)}`.trim()
                  : t("mentoringDetail.about.splitWindowSameAsDuration")
              }
            />
            {program?.contract_url && (
              <Row
                label={t("mentoringDetail.about.contract")}
                icon={<IconFileText size={14} />}
                value={
                  <a
                    className="cm-link"
                    href={program.contract_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Text.span left={<IconFileText size={14} />} contrast={7}>
                      {t("mentoringDetail.about.viewContract")}
                    </Text.span>
                  </a>
                }
              />
            )}
          </Card>
          {/* ── Community revenue: insights link + monthly chart ────────────── */}
          {revenue?.available && (
            <RevenueSection
              revenue={revenue.series}
              loading={revenue.loading}
              communityName={revenue.communityName}
              startDateLabel={revenue.startDateLabel}
              referenceDate={revenue.referenceDate}
              onOpenInsights={onOpenInsights}
            />
          )}
        </div>

        <div className="cm-col">
          <Card title={t("mentoringDetail.timelineCard.title")} icon={<IconTimeline size={16} />}>
            <MentoringTimeline timestamps={timestamps} />
            {enrollment.notes && (
              <div className="cm-notes">
                <Text.span contrast={5}>{t("mentoringDetail.timelineCard.notes")}</Text.span>
                <Text.p contrast={7}>{enrollment.notes}</Text.p>
              </div>
            )}
          </Card>

          {community && (
            <Card
              title={t("mentoringDetail.community.title")}
              icon={<IconBuildingCommunity size={16} />}
              className="cm-card--soft"
            >
              <div className="cm-community">
                <CommunityBanner banner={community.banner_url} size={56} />
                <div className="cm-community-info">
                  <Text.h5 contrast={7} weight={500} maxLines={1}>
                    {community.name}
                  </Text.h5>
                  <Text.span contrast={5}>{t("mentoringDetail.community.receiving")}</Text.span>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon, className = "", children }) {
  return (
    <div className={`cm-card${className ? ` ${className}` : ""}`}>
      <div className="cm-card-head">
        {icon && <span className="cm-card-icon">{icon}</span>}
        <Text.h5 contrast={8} weight={500}>
          {title}
        </Text.h5>
      </div>
      <div className="cm-card-body">{children}</div>
    </div>
  );
}

function Row({ label, value, icon, emphasis }) {
  return (
    <div className="cm-row">
      <Text.span contrast={5} left={icon}>
        {label}
      </Text.span>
      {typeof value === "string" ? (
        <Text.span contrast={emphasis ? 9 : 8} weight={emphasis ? 500 : 300}>
          {value}
        </Text.span>
      ) : (
        value
      )}
    </div>
  );
}

function fmtReadable(iso) {
  return iso ? Parser.date.readable(iso) : "—";
}
